import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AppState, BarcodeLookup, Can } from '../../shared/api';
import { EAN_RE } from '../../shared/catalog';
import { countryCode } from '../../shared/countries';
import { BARCODES_KEY, lookupBarcode, useBarcodes } from '../api';
import { CanImage } from '../components/CanImage';
import { sizeLabel } from '../format';
import { searchCans } from '../../shared/search';
import { Scanner, normalizeEan } from './Scanner';
import { Sheet } from '../components/Sheet';
import { ScanCan } from './PublicScan';
import { BarcodeCorrectionSheet } from './BarcodeCorrectionSheet';

interface Props {
  state: AppState;
  /** The barcode comes along when the can was found by its EAN. */
  onPick: (canId: number, barcode?: BarcodeLookup) => void;
  /** An EAN nobody knows yet: pick the can it belongs to. */
  onLink: (ean: string, suggestion: string | null) => void;
  onNewFlavor: (ean?: string) => void;
}

interface Result {
  can: Can;
  barcode?: BarcodeLookup;
}

const LIMIT = 40;

/** The scanner on top, below it one field for the whole catalog: by name or by EAN. */
export function Toevoegen({ state, onPick, onLink, onNewFlavor }: Props) {
  const qc = useQueryClient();
  const [query, setQuery] = useState('');
  const barcodes = useBarcodes();
  const [scan, setScan] = useState<{
    ean: string;
    hit?: BarcodeLookup;
    suggestion?: string | null;
    error?: string;
  } | null>(null);
  const [correcting, setCorrecting] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const scanLock = useRef(false);
  const sequence = useRef(0);
  useEffect(
    () => () => {
      sequence.current++;
    },
    [],
  );
  const dismissScan = () => {
    sequence.current++;
    scanLock.current = false;
    setScan(null);
    setCorrecting(false);
  };
  const detect = async (code: string, retry = false) => {
    if (scanLock.current && !retry) return;
    scanLock.current = true;
    const version = ++sequence.current;
    setScan({ ean: code });
    try {
      const found = await lookupBarcode(code);
      if (version !== sequence.current) return;
      setScan({ ean: code, ...(found.hit ? { hit: found.hit } : { suggestion: found.suggestion }) });
      if (found.hit) void qc.invalidateQueries({ queryKey: BARCODES_KEY });
    } catch (err) {
      if (version === sequence.current) setScan({ ean: code, error: (err as Error).message });
    }
  };
  // A correction can finish after the user dismisses its sheet. Keep this snapshot current too.
  useEffect(() => {
    setScan((current) => {
      if (!current?.hit) return current;
      const mapped = barcodes.data?.find((item) => item.ean === current.ean);
      return mapped &&
        (mapped.canId !== current.hit.canId ||
          mapped.country !== current.hit.country ||
          mapped.sizeMl !== current.hit.sizeMl)
        ? { ...current, hit: mapped }
        : current;
    });
  }, [barcodes.data]);

  const owned = useMemo(() => new Set(state.entries.map((e) => e.canId)), [state.entries]);
  const canById = useMemo(() => new Map(state.cans.map((c) => [c.id, c])), [state.cans]);
  const catName = useMemo(() => new Map(state.categories.map((c) => [c.id, c.name])), [state.categories]);

  const digits = query.replace(/\s+/g, '');
  const isCode = /^\d{3,}$/.test(digits);
  const ean = isCode && EAN_RE.test(normalizeEan(digits)) ? normalizeEan(digits) : null;
  const typed = query.trim().length > 0;

  const results = useMemo<Result[]>(() => {
    if (!typed) return [];
    if (!isCode) return searchCans(state.cans, state.categories, query, LIMIT).map((can) => ({ can }));
    const found: Result[] = [];
    for (const barcode of barcodes.data ?? []) {
      const can = canById.get(barcode.canId);
      if (can && (barcode.ean.includes(digits) || barcode.ean === ean)) found.push({ can, barcode });
      if (found.length === LIMIT) break;
    }
    return found;
  }, [typed, isCode, digits, ean, query, state.cans, state.categories, barcodes.data, canById]);

  // A complete EAN we have no can for: the server asks Open Food Facts.
  const known = ean !== null && (barcodes.data ?? []).some((b) => b.ean === ean);
  const lookup = useQuery({
    queryKey: ['barcode-lookup', ean],
    queryFn: async () => {
      const found = await lookupBarcode(ean!);
      if (found.hit) await qc.invalidateQueries({ queryKey: BARCODES_KEY });
      return found;
    },
    enabled: ean !== null && barcodes.isSuccess && !known,
    staleTime: Infinity,
    retry: false,
  });
  const unknown = ean !== null && !known && lookup.data?.hit === null ? lookup.data : null;
  const looking = ean !== null && !known && (barcodes.isPending || lookup.isFetching);
  const failed = ean !== null && !known && lookup.isError;

  return (
    <main className="screen screen--padded">
      <div className="add-page">
        <h1 className="title">TOEVOEGEN</h1>
        <div className="lede">Scan het blik, of zoek op naam of streepjescode.</div>

        <Scanner onDetected={(code) => void detect(code)} paused={!!scan} resetKey={resetKey} />

        <div className="search-field">
          <input
            className="input"
            type="search"
            enterKeyHint="search"
            autoComplete="off"
            placeholder="Naam of EAN"
            aria-label="Zoek een blik op naam of EAN"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && results[0]) {
                e.preventDefault();
                if (results[0].barcode) void detect(results[0].barcode.ean);
                else onPick(results[0].can.id);
              }
            }}
          />
        </div>

        <div className="pick-list pick-list--page" role="list">
          {results.map(({ can, barcode }) => (
            <button
              key={barcode ? barcode.ean : can.id}
              type="button"
              role="listitem"
              className="pick-row"
              onClick={() => (barcode ? void detect(barcode.ean) : onPick(can.id))}
            >
              <span className="pick-row__thumb">
                <CanImage imageId={can.imageId} empty="blank" />
              </span>
              <span className="pick-row__name">{can.name}</span>
              <span className="pick-row__cat">
                {barcode
                  ? [
                      barcode.ean,
                      barcode.country && countryCode(barcode.country),
                      barcode.sizeMl && sizeLabel(barcode.sizeMl),
                    ]
                      .filter(Boolean)
                      .join(' · ')
                  : catName.get(can.categoryId)}
                {owned.has(can.id) ? ' · ✓' : ''}
              </span>
            </button>
          ))}
        </div>

        {looking && <div className="pick-list__empty">Streepjescode opzoeken…</div>}
        {unknown && (
          <div className="note">
            EAN <span style={{ fontFamily: 'var(--mono)' }}>{ean}</span> kennen we nog niet
            {unknown.suggestion ? (
              <>
                {' '}
                (Open Food Facts noemt hem <q>{unknown.suggestion}</q>)
              </>
            ) : null}
            .
            <button
              type="button"
              className="btn"
              style={{ marginTop: 12, height: 48, fontSize: 15 }}
              onClick={() => onLink(ean!, unknown.suggestion)}
            >
              Koppel aan een blik
            </button>
          </div>
        )}
        {failed && <div className="error">{lookup.error?.message}</div>}
        {typed && results.length === 0 && !looking && !unknown && !failed && (
          <div className="pick-list__empty">
            {isCode ? 'Geen blik gevonden met die code.' : 'Geen blik gevonden met die naam.'}
          </div>
        )}

        {typed && (
          <button
            type="button"
            className="btn btn--outline"
            style={{ marginTop: 16, width: '100%' }}
            onClick={() => onNewFlavor(unknown ? ean! : undefined)}
          >
            Handmatig toevoegen
          </button>
        )}
      </div>
      {scan && !correcting && (
        <Sheet
          label={
            scan.hit
              ? 'Blik herkend'
              : scan.error
                ? 'Opzoeken mislukt'
                : scan.suggestion !== undefined
                  ? 'Onbekende streepjescode'
                  : 'Streepjescode opzoeken'
          }
          onClose={dismissScan}
        >
          {(close) => (
            <>
              <div className="label label--green">Scanresultaat</div>
              <h2 className="sheet__name">
                {scan.hit
                  ? 'Blik herkend'
                  : scan.error
                    ? 'Opzoeken mislukt'
                    : scan.suggestion !== undefined
                      ? 'Onbekende streepjescode'
                      : 'Streepjescode opzoeken…'}
              </h2>
              {scan.hit && canById.get(scan.hit.canId) ? (
                <>
                  <ScanCan can={canById.get(scan.hit.canId)!} barcode={scan.hit} />
                  {owned.has(scan.hit.canId) && (
                    <p className="note">
                      Deze smaak staat al in je collectie. Controleer land en inhoud voor je een variant toevoegt.
                    </p>
                  )}
                  <div className="scan-actions">
                    <button
                      type="button"
                      className="btn"
                      onClick={() => {
                        const hit = scan.hit!;
                        dismissScan();
                        onPick(hit.canId, hit);
                      }}
                    >
                      Toevoegen aan collectie
                    </button>
                    <button type="button" className="text-link" onClick={() => setCorrecting(true)}>
                      Dit is een ander blik · koppeling corrigeren
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="scan-can__ean">EAN {scan.ean}</div>
                  {scan.error && (
                    <>
                      <p className="error" role="alert">
                        {scan.error}
                      </p>
                      <button className="btn" type="button" onClick={() => void detect(scan.ean, true)}>
                        Opnieuw proberen
                      </button>
                    </>
                  )}
                  {scan.suggestion !== undefined && (
                    <>
                      <p className="note">
                        Deze code kennen we nog niet.
                        {scan.suggestion ? (
                          <>
                            {' '}
                            Open Food Facts noemt hem <q>{scan.suggestion}</q>.
                          </>
                        ) : (
                          ' Kies zelf de juiste smaak.'
                        )}
                      </p>
                      <button
                        type="button"
                        className="btn"
                        onClick={() => {
                          const { ean: unknownEan, suggestion } = scan;
                          dismissScan();
                          onLink(unknownEan, suggestion ?? null);
                        }}
                      >
                        Koppel aan een blik
                      </button>
                      <button
                        type="button"
                        className="text-link"
                        onClick={() => {
                          const unknownEan = scan.ean;
                          dismissScan();
                          onNewFlavor(unknownEan);
                        }}
                      >
                        Nieuwe smaak aanmaken
                      </button>
                    </>
                  )}
                </>
              )}
              <div className="scan-actions">
                <button
                  type="button"
                  className="btn btn--outline"
                  onClick={() => {
                    dismissScan();
                    setResetKey((key) => key + 1);
                  }}
                >
                  Volgend blik scannen
                </button>
                <button type="button" className="text-link" onClick={close}>
                  Sluiten
                </button>
              </div>
            </>
          )}
        </Sheet>
      )}
      {scan?.hit && correcting && (
        <BarcodeCorrectionSheet
          state={state}
          previous={scan.hit}
          onClose={() => setCorrecting(false)}
          onSaved={(barcode) => {
            setScan((current) => (current?.ean === barcode.ean ? { ean: barcode.ean, hit: barcode } : current));
            qc.setQueryData(['barcode-lookup', barcode.ean], { hit: barcode });
            setCorrecting(false);
          }}
        />
      )}
    </main>
  );
}
